<template>
    <v-container>
        <v-overlay color="iq_dark" :value="loading">
            <v-icon color="iq_light" x-large>mdi-loading mdi-spin</v-icon>
            <span class="iq_light--text font-weight-bold">Loading Validate IQ...</span>
        </v-overlay>

        <v-dialog v-model="showUnsavedChangesConfirm" max-width="420" persistent>
            <v-card>
                <v-card-title>Unsaved Changes</v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pt-4">
                    You have unsaved changes to your Phone Properties to Validate or Phone Formatting Options. They
                    won't be saved unless you go back and click Save on that card. Continue anyway?
                </v-card-text>
                <v-card-actions class="px-4">
                    <v-spacer></v-spacer>
                    <v-btn text @click="cancelPendingAction">Cancel</v-btn>
                    <v-btn color="primary" @click="confirmPendingAction">Continue</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-row>
            <v-col cols="12" class="d-flex align-center">
                <img src="@/assets/images/logo-validateiq-pink.png" alt="Validate IQ" height="48" class="mr-3">
                <div>
                    <h1>Validate IQ</h1>
                    <p class="mb-0">Validate HubSpot contact phone numbers with configurable formatting options.</p>
                </div>
            </v-col>
        </v-row>

        <v-row v-if="!loading && !status.hubspotConnected" justify="center">
            <v-col cols="12" md="9" lg="7">
                <v-card outlined class="text-center">
                    <v-card-text class="pt-8 pb-6">
                        <v-avatar color="primary lighten-5" size="72" class="mb-4">
                            <v-icon color="primary" size="36">$hubspotIcon</v-icon>
                        </v-avatar>
                        <h2 class="mb-2">Connect to HubSpot</h2>
                        <p class="grey--text text--darken-1 mb-0">
                            Connect your HubSpot account so Validate IQ can check and format your contacts' phone numbers.
                        </p>
                    </v-card-text>

                    <v-divider></v-divider>

                    <v-card-text class="text-left">
                        <p class="mb-4">
                            Connecting creates a <strong>Validate IQ</strong> property group on the HubSpot
                            <strong>Contact</strong> object, with the properties below. Phone and Mobile Phone
                            validation are on by default - you can turn either off, or add up to 3 custom
                            properties, from the settings once you're connected.
                        </p>

                        <v-row>
                            <v-col
                                v-for="group in hubspotDefaultPropertyGroups"
                                :key="group.label"
                                cols="12"
                                sm="6"
                            >
                                <div class="font-weight-medium mb-1">{{ group.label }}</div>
                                <v-list dense class="py-0">
                                    <v-list-item
                                        v-for="property in group.properties"
                                        :key="property.label"
                                        class="px-0"
                                    >
                                        <v-list-item-icon class="mr-2 my-2 align-self-start">
                                            <v-icon small color="primary">mdi-tag-outline</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-content class="py-1">
                                            <div class="text-body-2 font-weight-medium">{{ property.label }}</div>
                                            <div class="caption grey--text text--darken-1">{{ property.description }}</div>
                                        </v-list-item-content>
                                    </v-list-item>
                                </v-list>
                            </v-col>
                        </v-row>

                        <p class="caption grey--text text--darken-1 mt-2 mb-0">
                            Adding a custom property to validate later creates this same set of properties named after it,
                            e.g. Validate IQ Another Phone Status for a custom property labeled "Another Phone".
                        </p>
                    </v-card-text>

                    <v-divider></v-divider>

                    <v-card-actions class="px-4 py-4 d-flex flex-column align-center">
                        <v-btn color="primary" large :loading="connectingToHubSpot" @click="connectHubSpot">
                            <v-icon class="mr-2">$hubspotIcon</v-icon>
                            Connect HubSpot
                        </v-btn>
                        <div class="caption grey--text text--darken-1 mt-2 text-center">
                            This connects your HubSpot account - required before Validate IQ can start validating phone numbers.
                        </div>
                    </v-card-actions>
                </v-card>
            </v-col>
        </v-row>

        <v-row v-if="!loading && status.hubspotConnected && !status.propertiesCreated">
            <v-col cols="12">
                <v-card outlined>
                    <v-card-title>Sync Validate IQ Properties</v-card-title>
                    <v-card-subtitle>
                        HubSpot is connected, but Validate IQ properties are not ready yet.
                    </v-card-subtitle>
                    <v-divider></v-divider>
                    <v-card-actions class="px-4">
                        <v-btn color="primary" :loading="syncingProperties" @click="syncProperties">
                            <v-icon class="mr-2">mdi-sync</v-icon>
                            Sync Contact Properties
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-col>
        </v-row>

        <template v-if="!loading && status.hubspotConnected && status.propertiesCreated">
            <v-tabs v-model="activeTab" class="mb-4">
                <v-tab>Validate</v-tab>
                <v-tab>Settings</v-tab>
                <v-tab>Billing</v-tab>
            </v-tabs>

            <v-tabs-items v-model="activeTab">
            <v-tab-item>
            <v-row>
                <v-col cols="12" md="6">
                    <v-card outlined class="d-flex flex-column fill-height">
                        <v-card-title class="d-flex justify-space-between align-center">
                            <span>Validate Specific Contact</span>
                            <v-btn icon small @click="showSingleContactHelp = true">
                                <v-icon>mdi-help-circle-outline</v-icon>
                            </v-btn>
                        </v-card-title>
                        <v-card-subtitle>Validate one contact by id or URL.</v-card-subtitle>
                        <v-divider></v-divider>
                        <v-card-text class="flex-grow-1">
                            <v-text-field
                                v-model="singleContactId"
                                label="HubSpot contact id or contact URL"
                                hide-details
                                dense
                            ></v-text-field>
                            <v-alert v-if="singleContactResult" :type="singleContactResult.success ? 'success' : 'error'" dense outlined class="mb-0 mt-4">
                                {{ singleContactResult.success ? 'Contact validation completed.' : singleContactResult.errorMessage || 'Contact validation failed.' }}
                            </v-alert>
                        </v-card-text>
                        <v-card-actions class="px-4">
                            <v-btn color="primary" :loading="validatingSingleContact" @click="guardedValidateSingleContact">
                                Validate Contact
                            </v-btn>
                        </v-card-actions>
                    </v-card>

                    <v-dialog v-model="showSingleContactHelp" max-width="500">
                        <v-card>
                            <v-card-title>Validate Specific Contact</v-card-title>
                            <v-divider></v-divider>
                            <v-card-text class="pt-4">
                                <p>
                                    Validates all of your configured phone properties - Phone, Mobile Phone, and any custom
                                    properties - for a single HubSpot contact, formatting valid numbers according to your
                                    Phone Formatting Options.
                                </p>
                                <p class="mb-0">
                                    Enter the HubSpot contact id, e.g. <strong>99999999</strong>, or paste the full contact URL
                                    and the id will be extracted automatically, e.g.
                                    <strong>https://app.hubspot.com/contacts/999999/record/0-1/99999999</strong>.
                                </p>
                            </v-card-text>
                            <v-card-actions class="px-4">
                                <v-spacer></v-spacer>
                                <v-btn text @click="showSingleContactHelp = false">Close</v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-dialog>
                </v-col>

                <v-col cols="12" md="6">
                    <v-card outlined class="d-flex flex-column fill-height">
                        <v-card-title class="d-flex justify-space-between align-center">
                            <span>Validate Contacts</span>
                            <v-btn icon small @click="showValidateContactsHelp = true">
                                <v-icon>mdi-help-circle-outline</v-icon>
                            </v-btn>
                        </v-card-title>
                        <v-card-subtitle>Validate every contact, or just one segment.</v-card-subtitle>
                        <v-divider></v-divider>
                        <v-card-text class="flex-grow-1"></v-card-text>
                        <v-card-actions class="px-4">
                            <v-btn color="primary" :disabled="!!activeRun" @click="guardedOpenValidateContactsDialog">
                                Validate Contacts
                            </v-btn>
                        </v-card-actions>
                    </v-card>

                    <v-dialog v-model="showValidateContactsHelp" max-width="500">
                        <v-card>
                            <v-card-title>Validate Contacts</v-card-title>
                            <v-divider></v-divider>
                            <v-card-text class="pt-4">
                                <p>
                                    Validates all of your configured phone properties - Phone, Mobile Phone, and any custom
                                    properties - for every contact in your HubSpot portal, or just the contacts in one
                                    segment (list), formatting valid numbers according to your Phone Formatting Options.
                                </p>
                                <p class="mb-0">
                                    Before starting, you'll see an estimated validation count. Continuous Plan subscribers
                                    are covered automatically; otherwise it draws from your credit balance, purchasing more
                                    first if needed. Only one sync - full or segment - can run at a time for your account.
                                </p>
                            </v-card-text>
                            <v-card-actions class="px-4">
                                <v-spacer></v-spacer>
                                <v-btn text @click="showValidateContactsHelp = false">Close</v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-dialog>

                    <v-dialog v-model="showValidateContactsDialog" max-width="560" persistent>
                        <v-card>
                            <v-card-title class="d-flex align-center">
                                <v-icon color="primary" class="mr-2">mdi-shield-check-outline</v-icon>
                                Validate Contacts
                                <v-spacer></v-spacer>
                                <v-btn icon small @click="closeValidateContactsDialog">
                                    <v-icon>mdi-close</v-icon>
                                </v-btn>
                            </v-card-title>
                            <v-card-subtitle>Validate every contact, or just one segment.</v-card-subtitle>
                            <v-divider></v-divider>
                            <v-card-text class="pt-5">
                                <div class="text-overline grey--text text--darken-1 mb-2" style="line-height: 1.2;">
                                    What do you want to validate?
                                </div>
                                <v-btn-toggle v-model="validateScope" mandatory color="primary" dense class="mb-4" style="width: 100%;">
                                    <v-btn value="all" style="flex: 1;">
                                        <v-icon left small>mdi-account-group-outline</v-icon>
                                        All Contacts
                                    </v-btn>
                                    <v-btn value="segment" style="flex: 1;">
                                        <v-icon left small>mdi-filter-variant</v-icon>
                                        A Segment
                                    </v-btn>
                                </v-btn-toggle>

                                <v-autocomplete
                                    v-if="validateScope === 'segment'"
                                    v-model="selectedSegmentId"
                                    :items="segments"
                                    :loading="loadingSegments"
                                    item-text="name"
                                    item-value="listId"
                                    label="HubSpot segment"
                                    prepend-inner-icon="mdi-format-list-bulleted"
                                    outlined
                                    dense
                                    clearable
                                    class="mb-2"
                                >
                                    <template v-slot:item="{ item }">
                                        {{ item.name }}
                                        <span v-if="item.memberCount !== null && item.memberCount !== undefined" class="grey--text ml-1">({{ item.memberCount.toLocaleString() }})</span>
                                    </template>
                                    <template v-slot:selection="{ item }">
                                        {{ item.name }}
                                        <span v-if="item.memberCount !== null && item.memberCount !== undefined" class="grey--text ml-1">({{ item.memberCount.toLocaleString() }})</span>
                                    </template>
                                </v-autocomplete>

                                <v-btn
                                    v-if="!estimate"
                                    block
                                    color="primary"
                                    :disabled="validateScope === 'segment' && !selectedSegmentId"
                                    :loading="loadingEstimate"
                                    class="mt-2"
                                    @click="loadEstimate"
                                >
                                    <v-icon left small>mdi-calculator-variant</v-icon>
                                    Get Estimate
                                </v-btn>

                                <v-sheet v-if="estimate" outlined rounded class="pa-4 mt-2">
                                    <div class="d-flex align-baseline">
                                        <span class="text-h3 font-weight-bold">{{ estimate.estimatedCount.toLocaleString() }}</span>
                                        <span class="grey--text ml-2">estimated validations</span>
                                    </div>

                                    <v-alert v-if="estimate.continuousPlanActive" type="success" dense outlined class="mb-0 mt-4">
                                        Covered by your Continuous Plan.
                                    </v-alert>
                                    <v-alert v-else-if="estimate.shortfallCount <= 0" type="success" dense outlined class="mb-0 mt-4">
                                        Covered by your free monthly validations.
                                    </v-alert>
                                    <template v-else>
                                        <v-divider class="my-4"></v-divider>
                                        <div class="d-flex align-center justify-space-between mb-1">
                                            <span class="grey--text text--darken-1">Remaining free this month</span>
                                            <span class="font-weight-medium">{{ estimate.remainingFreeValidations.toLocaleString() }}</span>
                                        </div>
                                        <v-alert type="warning" dense outlined class="mb-0 mt-3">
                                            You need to purchase <strong>{{ estimate.shortfallCount.toLocaleString() }}</strong> more validations
                                            (<strong>{{ formatCents(estimate.shortfallPriceCents) }}</strong>) to run this sync.
                                        </v-alert>
                                    </template>
                                </v-sheet>
                            </v-card-text>
                            <v-divider v-if="estimate"></v-divider>
                            <v-card-actions class="px-4 py-3">
                                <v-btn text @click="closeValidateContactsDialog">Cancel</v-btn>
                                <v-spacer></v-spacer>
                                <template v-if="estimate">
                                    <v-btn
                                        v-if="estimate.continuousPlanActive || estimate.shortfallCount <= 0"
                                        color="primary"
                                        :loading="startingValidation"
                                        @click="startValidation"
                                    >
                                        <v-icon left small>mdi-play</v-icon>
                                        Start Sync
                                    </v-btn>
                                    <v-btn
                                        v-else
                                        color="primary"
                                        :loading="purchasingAndStarting"
                                        @click="purchaseAndStart"
                                    >
                                        <v-icon left small>mdi-cart-outline</v-icon>
                                        Purchase &amp; Start
                                    </v-btn>
                                </template>
                            </v-card-actions>
                        </v-card>
                    </v-dialog>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12" v-if="activeRun">
                    <v-card outlined>
                        <v-card-title>Active Sync Progress</v-card-title>
                        <v-card-subtitle>
                            Run: {{ activeRun.syncRunId }} | Status: {{ activeRun.status }} | Scope: {{ activeRun.hubSpotListName || 'All Contacts' }}
                        </v-card-subtitle>
                        <v-divider></v-divider>
                        <v-card-text>
                            <v-progress-linear :value="activeRunProgress" color="primary" height="16" rounded>
                                <strong>{{ activeRunProgress }}%</strong>
                            </v-progress-linear>
                            <v-row class="mt-3">
                                <v-col cols="6" md="2">Scanned: <strong>{{ activeRun.contactsScanned }}</strong></v-col>
                                <v-col cols="6" md="2">With phone: <strong>{{ activeRun.contactsWithPhone }}</strong></v-col>
                                <v-col cols="6" md="2">Updated: <strong>{{ activeRun.contactsUpdated }}</strong></v-col>
                                <v-col cols="6" md="2">Failed: <strong>{{ activeRun.contactsFailed }}</strong></v-col>
                                <v-col cols="6" md="2">Batches done: <strong>{{ activeRun.bulkRequestsCompleted }}</strong></v-col>
                                <v-col cols="6" md="2">Batches in progress: <strong>{{ activeRun.bulkRequestsInProgress }}</strong></v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>
                </v-col>

                <v-col cols="12">
                    <v-card outlined>
                        <v-card-title class="d-flex justify-space-between align-center">
                            <span>Last Sync Runs</span>
                            <v-btn small text color="primary" :loading="loadingDashboard" @click="loadSyncDashboard">Refresh</v-btn>
                        </v-card-title>
                        <v-divider></v-divider>
                        <v-data-table
                            :headers="historyHeaders"
                            :items="recentRuns"
                            :items-per-page="-1"
                            hide-default-footer
                            dense
                        >
                            <template v-slot:item.status="{ item }">
                                <v-chip x-small :color="statusColor(item.status)" dark>{{ item.status }}</v-chip>
                            </template>
                            <template v-slot:item.scope="{ item }">
                                {{ item.hubSpotListName || 'All Contacts' }}
                            </template>
                            <template v-slot:item.startedAt="{ item }">
                                {{ formatDate(item.startedAt) }}
                            </template>
                            <template v-slot:item.createdAt="{ item }">
                                {{ formatDate(item.createdAt) }}
                            </template>
                            <template v-slot:item.completedAt="{ item }">
                                {{ formatDate(item.completedAt) }}
                            </template>
                        </v-data-table>
                    </v-card>
                </v-col>
            </v-row>
            </v-tab-item>

            <v-tab-item>
            <v-row>
                <v-col cols="12" md="6">
                    <v-card outlined class="mb-4">
                        <v-card-title class="d-flex justify-space-between align-center">
                            <span>Phone Properties to Validate</span>
                            <v-btn icon small @click="showPhonePropertiesHelp = true">
                                <v-icon>mdi-help-circle-outline</v-icon>
                            </v-btn>
                        </v-card-title>
                        <v-card-subtitle>Choose which phone properties to validate.</v-card-subtitle>
                        <v-divider></v-divider>
                        <v-card-text>
                            <v-switch v-model="phoneProperties.validatePhone" label="Phone" hide-details class="mb-2"></v-switch>
                            <v-switch v-model="phoneProperties.validateMobilePhone" label="Mobile Phone" hide-details class="mb-4"></v-switch>

                            <div class="d-flex flex-wrap align-center">
                                <v-chip
                                    v-for="customProperty in phoneProperties.customProperties"
                                    :key="customProperty"
                                    class="mr-2 mb-2"
                                    close
                                    @click:close="removeCustomProperty(customProperty)"
                                >
                                    {{ propertyLabel(customProperty) }}
                                </v-chip>
                                <v-btn
                                    v-if="phoneProperties.customProperties.length < 3"
                                    icon
                                    small
                                    outlined
                                    class="mb-2"
                                    title="Add a custom property"
                                    @click="openPropertyPicker"
                                >
                                    <v-icon>mdi-plus</v-icon>
                                </v-btn>
                            </div>
                            <v-alert v-if="!hasAnyPhoneProperty" type="warning" dense outlined class="mb-0 mt-4">
                                At least one property must be selected.
                            </v-alert>
                        </v-card-text>
                        <template v-if="phonePropertiesDirty">
                            <v-divider></v-divider>
                            <v-card-actions class="px-4">
                                <v-btn text small :disabled="savingPhoneProperties" @click="cancelPhonePropertiesOptions">
                                    Cancel
                                </v-btn>
                                <v-spacer></v-spacer>
                                <v-btn
                                    color="primary"
                                    small
                                    :loading="savingPhoneProperties"
                                    :disabled="!hasAnyPhoneProperty"
                                    @click="savePhonePropertiesOptions"
                                >
                                    Save
                                </v-btn>
                            </v-card-actions>
                        </template>
                    </v-card>

                    <v-dialog v-model="showPhonePropertiesHelp" max-width="500">
                        <v-card>
                            <v-card-title>Phone Properties to Validate</v-card-title>
                            <v-divider></v-divider>
                            <v-card-text class="pt-4">
                                <p>
                                    Choose which properties on a HubSpot contact Validate IQ checks and formats: the built-in
                                    <strong>Phone</strong> and <strong>Mobile Phone</strong> properties, plus up to 3 custom properties
                                    you pick from your HubSpot schema.
                                </p>
                                <p>
                                    Custom properties are validated when a contact is created (if Continuous Monitoring is on), when you use
                                    <strong>Validate Specific Contact</strong>, and during <strong>Validate All Contacts</strong> or
                                    <strong>Validate Segment</strong>. Continuous Monitoring does not revalidate a custom property when it's
                                    edited afterward - only Phone and Mobile Phone get validated live on every change.
                                </p>
                                <p class="mb-0">
                                    Adding a custom property creates a matching set of diagnostic properties named after it, e.g.
                                    a property labeled <strong>Another Phone</strong> gets its own
                                    <strong>Validate IQ Another Phone Status</strong>, <strong>Line Type</strong>,
                                    <strong>Carrier</strong>, and <strong>Last Validated At</strong> properties.
                                </p>
                            </v-card-text>
                            <v-card-actions class="px-4">
                                <v-spacer></v-spacer>
                                <v-btn text @click="showPhonePropertiesHelp = false">Close</v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-dialog>

                    <v-dialog v-model="showPropertyPicker" max-width="480">
                        <v-card>
                            <v-card-title>Add Custom Property</v-card-title>
                            <v-divider></v-divider>
                            <v-card-text class="pt-4">
                                <v-autocomplete
                                    v-model="selectedPropertyToAdd"
                                    :items="pickablePropertyOptions"
                                    :loading="loadingAvailableProperties"
                                    item-text="label"
                                    item-value="name"
                                    label="Contact property"
                                    dense
                                    clearable
                                ></v-autocomplete>
                            </v-card-text>
                            <v-card-actions class="px-4">
                                <v-spacer></v-spacer>
                                <v-btn text @click="showPropertyPicker = false">Cancel</v-btn>
                                <v-btn color="primary" text :disabled="!selectedPropertyToAdd" @click="addCustomProperty">Add</v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-dialog>

                    <v-card outlined>
                        <v-card-title class="d-flex justify-space-between align-center">
                            <span>Phone Formatting Options</span>
                            <v-btn icon small @click="showFormattingExamples = true">
                                <v-icon>mdi-help-circle-outline</v-icon>
                            </v-btn>
                        </v-card-title>
                        <v-card-subtitle>Format valid phone numbers automatically.</v-card-subtitle>
                        <v-divider></v-divider>
                        <v-card-text>
                            <v-switch v-model="phoneFormatting.formatNumbers" label="Format Numbers"></v-switch>
                            <template v-if="phoneFormatting.formatNumbers">
                                <v-switch v-model="phoneFormatting.addCountryCodeEnabled" label="Add country code (+1)"></v-switch>
                                <v-switch v-model="phoneFormatting.preserveExtension" label="Preserve extension"></v-switch>
                            </template>
                        </v-card-text>
                        <template v-if="phoneFormattingDirty">
                            <v-divider></v-divider>
                            <v-card-actions class="px-4">
                                <v-btn text small :disabled="savingPhoneFormatting" @click="cancelPhoneFormattingOptions">
                                    Cancel
                                </v-btn>
                                <v-spacer></v-spacer>
                                <v-btn color="primary" small :loading="savingPhoneFormatting" @click="savePhoneFormattingOptions">
                                    Save
                                </v-btn>
                            </v-card-actions>
                        </template>
                    </v-card>

                    <v-dialog v-model="showFormattingExamples" max-width="500">
                        <v-card>
                            <v-card-title>Formatting Examples</v-card-title>
                            <v-divider></v-divider>
                            <v-card-text class="pt-4">
                                <div class="mb-3">
                                    <div class="grey--text text--darken-1">Format Numbers</div>
                                    <div>
                                        When off, the phone number in HubSpot is never updated.<br>When on, any number Bandwidth confirms
                                        as valid is always formatted the same combined way:
                                        <code>5559999999</code> &rarr; <code>(555) 999-9999</code>.<br>Invalid numbers are left unchanged.
                                    </div>
                                </div>
                                <div class="mb-3">
                                    <div class="grey--text text--darken-1">Add country code (+1)</div>
                                    <div><code>5559999999</code> &rarr; <code>+1 (555) 999-9999</code></div>
                                </div>
                                <div class="mb-3">
                                    <div class="grey--text text--darken-1">Preserve extension</div>
                                    <div class="mb-1"><code>5559999999 x123</code> &rarr; <code>(555) 999-9999 ext123</code></div>
                                    <div class="mb-1"><code>5559999999 ext 123</code> &rarr; <code>(555) 999-9999 ext123</code></div>
                                    <div><code>5559999999 123</code> &rarr; <code>(555) 999-9999 ext123</code></div>
                                </div>
                                <v-divider class="mb-3"></v-divider>
                                <div>
                                    <div class="grey--text text--darken-1">All three together</div>
                                    <div><code>5559999999 x123</code> &rarr; <code>+1 (555) 999-9999 ext123</code></div>
                                </div>
                            </v-card-text>
                            <v-card-actions class="px-4">
                                <v-spacer></v-spacer>
                                <v-btn text @click="showFormattingExamples = false">Close</v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-dialog>
                </v-col>

                <v-col cols="12" md="6">
                    <v-card outlined>
                        <v-card-title class="d-flex justify-space-between align-center">
                            <div class="d-flex align-center">
                                <span>Continuous Monitoring</span>
                                <v-switch
                                    v-model="liveSyncEnabled"
                                    inset
                                    hide-details
                                    class="ma-0 pa-0 ml-3"
                                    :disabled="savingLiveSync || !usage.continuousPlanActive"
                                ></v-switch>
                            </div>
                            <v-btn icon small @click="showLiveSyncHelp = true">
                                <v-icon>mdi-help-circle-outline</v-icon>
                            </v-btn>
                        </v-card-title>
                        <v-card-subtitle>Automatically validate phone numbers when contacts are created or when their phone or mobile phone numbers are updated.</v-card-subtitle>
                        <v-card-text v-if="!usage.continuousPlanActive" class="pt-0 pb-3">
                            <span class="caption grey--text text--darken-1">Requires an active Continuous Plan - see the Billing tab.</span>
                        </v-card-text>
                        <template v-if="liveSyncDirty">
                            <v-divider></v-divider>
                            <v-card-actions class="px-4">
                                <v-btn text small :disabled="savingLiveSync" @click="cancelLiveSync">
                                    Cancel
                                </v-btn>
                                <v-spacer></v-spacer>
                                <v-btn color="primary" small :loading="savingLiveSync" @click="saveLiveSync">
                                    Save
                                </v-btn>
                            </v-card-actions>
                        </template>
                    </v-card>

                    <v-dialog v-model="showLiveSyncHelp" max-width="500">
                        <v-card>
                            <v-card-title>Continuous Monitoring</v-card-title>
                            <v-divider></v-divider>
                            <v-card-text class="pt-4">
                                <p>
                                    Continuous Monitoring listens for HubSpot events and validates a contact's phone in near real time,
                                    without waiting for a manual validation or a full sync:
                                </p>
                                <div class="mb-3">
                                    <div class="grey--text text--darken-1">Contact created</div>
                                    <div>Every configured phone property already populated on the new contact - Phone, Mobile Phone, and any custom properties - is validated.</div>
                                </div>
                                <div class="mb-3">
                                    <div class="grey--text text--darken-1">Phone or Mobile Phone updated</div>
                                    <div>Only the property that changed is revalidated immediately.</div>
                                </div>
                                <div>
                                    <div class="grey--text text--darken-1">Custom properties</div>
                                    <div>Only validated when a contact is first created - editing a custom property afterward does not trigger Continuous Monitoring. Use <strong>Validate Specific Contact</strong>, <strong>Validate All Contacts</strong>, or <strong>Validate Segment</strong> to revalidate it.</div>
                                </div>
                            </v-card-text>
                            <v-card-actions class="px-4">
                                <v-spacer></v-spacer>
                                <v-btn text @click="showLiveSyncHelp = false">Close</v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-dialog>
                </v-col>
            </v-row>
            </v-tab-item>

            <v-tab-item>
            <v-row>
                <v-col cols="12" md="6">
                    <v-card outlined>
                        <v-card-title class="d-flex justify-space-between align-center">
                            <span>This Month</span>
                            <v-progress-circular v-if="loadingUsage" indeterminate size="18" width="2" color="primary"></v-progress-circular>
                        </v-card-title>
                        <v-card-subtitle>{{ currentMonthLabel }}</v-card-subtitle>
                        <v-divider></v-divider>
                        <v-card-text>
                            <div class="d-flex align-baseline flex-wrap">
                                <span class="text-h3 font-weight-bold">{{ usage.currentMonthUsed }}</span>
                                <span class="grey--text ml-2" v-if="usage.continuousPlanActive && usage.monthlyValidationCap === null">
                                    validations (unlimited plan)
                                </span>
                                <span class="grey--text ml-2" v-else-if="usage.continuousPlanActive">
                                    / {{ usage.monthlyValidationCap }} validations (monthly cap)
                                </span>
                                <span class="grey--text ml-2" v-else>/ {{ usage.monthlyFreeLimit }} free validations</span>
                            </div>
                            <v-progress-linear
                                v-if="!(usage.continuousPlanActive && usage.monthlyValidationCap === null)"
                                :value="usagePercent"
                                :color="usagePercent >= 100 ? 'error' : 'primary'"
                                height="10"
                                rounded
                                class="mt-4"
                            ></v-progress-linear>
                            <div v-if="!usage.continuousPlanActive" class="caption grey--text text--darken-1 mt-2 mb-0">
                                Once the free monthly limit is reached, a sync must purchase validations to cover the
                                difference, unless you have a Continuous Plan.
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col>

                <v-col cols="12" md="6">
                    <v-card outlined>
                        <v-card-title class="text-body-1">Previous Month</v-card-title>
                        <v-card-subtitle>{{ previousMonthLabel }}</v-card-subtitle>
                        <v-divider></v-divider>
                        <v-card-text>
                            <span class="text-h5 font-weight-medium">{{ usage.previousMonthUsed }}</span>
                            <span class="grey--text ml-1">validations</span>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12">
                    <v-card outlined class="d-flex flex-column fill-height">
                        <v-card-title>Continuous Plan</v-card-title>
                        <v-divider></v-divider>
                        <v-card-text class="flex-grow-1">
                            <v-chip :color="usage.continuousPlanActive ? 'success' : 'grey lighten-2'" :dark="usage.continuousPlanActive" label>
                                {{ usage.continuousPlanActive ? 'Active' : 'Inactive' }}
                            </v-chip>
                            <div class="mt-3" v-if="usage.continuousPlanActive">
                                Billed monthly based on validations used, at the volume pricing below. This also unlocks
                                Continuous Monitoring.
                            </div>
                            <div class="mt-3" v-else>
                                Pay monthly for validations actually used, at the volume pricing below, with no need to
                                purchase validations ahead of time. Also required to turn on Continuous Monitoring.
                            </div>

                            <template v-if="usage.continuousPlanActive">
                                <v-divider class="my-4"></v-divider>
                                <v-text-field
                                    v-model.number="monthlyValidationCap"
                                    label="Monthly validation cap (optional)"
                                    hint="Leave blank for no cap"
                                    persistent-hint
                                    type="number"
                                    min="0"
                                    dense
                                ></v-text-field>
                            </template>
                        </v-card-text>
                        <template v-if="usage.continuousPlanActive && monthlyValidationCapDirty">
                            <v-divider></v-divider>
                            <v-card-actions class="px-4">
                                <v-btn text small :disabled="savingMonthlyCap" @click="cancelMonthlyValidationCap">Cancel</v-btn>
                                <v-spacer></v-spacer>
                                <v-btn color="primary" small :loading="savingMonthlyCap" @click="saveMonthlyValidationCap">Save</v-btn>
                            </v-card-actions>
                        </template>
                        <v-card-actions class="px-4" v-if="!usage.continuousPlanActive">
                            <v-btn color="primary" :loading="startingContinuousPlanCheckout" @click="startContinuousPlanCheckout">
                                Enable Continuous Plan
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12">
                    <v-card outlined>
                        <v-card-title>Pricing</v-card-title>
                        <v-card-subtitle>Price per validation, based on total validations used that month.</v-card-subtitle>
                        <v-divider></v-divider>
                        <v-card-text>
                            <v-row>
                                <v-col v-for="tier in pricingTiersDisplay" :key="tier.range" cols="6" sm="4" md="3">
                                    <v-card outlined class="text-center fill-height d-flex flex-column">
                                        <v-card-text class="d-flex flex-column flex-grow-1">
                                            <div class="text-overline grey--text text--darken-1 mb-1">Monthly volume</div>
                                            <div class="text-subtitle-1 font-weight-bold">{{ tier.range }}</div>
                                            <div class="mt-3">
                                                <span class="text-h5 font-weight-bold primary--text">{{ tier.price }}</span>
                                                <span class="grey--text text--darken-1">&nbsp;/check</span>
                                            </div>
                                            <v-spacer></v-spacer>
                                            <div class="mt-3">
                                                <v-chip v-if="tier.badge" x-small :color="tier.badgeColor" :text-color="tier.badgeTextColor">
                                                    {{ tier.badge }}
                                                </v-chip>
                                            </div>
                                        </v-card-text>
                                    </v-card>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12">
                    <v-card outlined>
                        <v-card-title class="d-flex justify-space-between align-center">
                            <span>Invoices</span>
                            <v-btn small text color="primary" :loading="loadingInvoices" @click="loadInvoices">Refresh</v-btn>
                        </v-card-title>
                        <v-divider></v-divider>
                        <v-data-table
                            :headers="invoiceHeaders"
                            :items="invoices"
                            :items-per-page="-1"
                            hide-default-footer
                            dense
                        >
                            <template v-slot:item.type="{ item }">
                                {{ item.type === 'SUBSCRIPTION' ? 'Continuous Plan' : 'Validation Purchase' }}
                            </template>
                            <template v-slot:item.amountCents="{ item }">
                                {{ formatCents(item.amountCents) }}
                            </template>
                            <template v-slot:item.issuedAt="{ item }">
                                {{ formatDate(item.issuedAt) }}
                            </template>
                            <template v-slot:item.hostedInvoiceUrl="{ item }">
                                <a v-if="item.hostedInvoiceUrl" :href="item.hostedInvoiceUrl" target="_blank" rel="noopener">View</a>
                                <span v-else class="grey--text">-</span>
                            </template>
                        </v-data-table>
                    </v-card>
                </v-col>
            </v-row>
            </v-tab-item>
            </v-tabs-items>
        </template>
    </v-container>
</template>

<script>
export default {
    data() {
        return {
            loading: true,
            connectingToHubSpot: false,
            hubspotDefaultPropertyGroups: [
                {
                    label: 'Phone',
                    properties: [
                        { label: 'Validate IQ Phone Status', description: 'Last validation result for Phone - a Valid / Invalid / Unknown dropdown.' },
                        { label: 'Validate IQ Phone Line Type', description: "The Phone number's line type returned by Bandwidth (e.g. mobile, landline, voip)." },
                        { label: 'Validate IQ Phone Carrier', description: "The Phone number's carrier returned by Bandwidth." },
                        { label: 'Validate IQ Phone Last Validated At', description: 'When Phone was last validated.' }
                    ]
                },
                {
                    label: 'Mobile Phone',
                    properties: [
                        { label: 'Validate IQ Mobile Phone Status', description: 'Last validation result for Mobile Phone - a Valid / Invalid / Unknown dropdown.' },
                        { label: 'Validate IQ Mobile Phone Line Type', description: "The Mobile Phone number's line type returned by Bandwidth." },
                        { label: 'Validate IQ Mobile Phone Carrier', description: "The Mobile Phone number's carrier returned by Bandwidth." },
                        { label: 'Validate IQ Mobile Phone Last Validated At', description: 'When Mobile Phone was last validated.' }
                    ]
                }
            ],
            syncingProperties: false,
            validatingSingleContact: false,
            loadingDashboard: false,
            segments: [],
            loadingSegments: false,
            selectedSegmentId: null,
            validateScope: 'all',
            showValidateContactsHelp: false,
            showValidateContactsDialog: false,
            estimate: null,
            loadingEstimate: false,
            startingValidation: false,
            showFormattingExamples: false,
            savingPhoneFormatting: false,
            savedPhoneFormatting: null,
            phoneProperties: {
                validatePhone: true,
                validateMobilePhone: true,
                customProperties: []
            },
            savedPhoneProperties: null,
            savingPhoneProperties: false,
            showPropertyPicker: false,
            showPhonePropertiesHelp: false,
            loadingAvailableProperties: false,
            availableProperties: [],
            selectedPropertyToAdd: null,
            liveSyncEnabled: false,
            savedLiveSyncEnabled: null,
            savingLiveSync: false,
            showLiveSyncHelp: false,
            status: {
                hubspotConnected: false,
                propertiesCreated: false
            },
            singleContactId: '',
            singleContactResult: null,
            showSingleContactHelp: false,
            dashboard: {
                activeRun: null,
                recentRuns: []
            },
            phoneFormatting: {
                formatNumbers: false,
                addCountryCodeEnabled: false,
                preserveExtension: false
            },
            poller: null,
            showUnsavedChangesConfirm: false,
            pendingConfirmedAction: null,
            activeTab: 0,
            billingTabIndex: 2, // matches the "Billing" v-tab's position (Validate, Settings, Billing)
            loadingUsage: false,
            usage: {
                paymentConfigured: false,
                monthlyFreeLimit: 100,
                currentYear: null,
                currentMonth: null,
                currentMonthUsed: 0,
                previousYear: null,
                previousMonth: null,
                previousMonthUsed: 0,
                continuousPlanActive: false,
                monthlyValidationCap: null
            },
            monthlyValidationCap: null,
            savedMonthlyValidationCap: null,
            savingMonthlyCap: false,
            startingContinuousPlanCheckout: false,
            purchasingAndStarting: false,
            loadingInvoices: false,
            invoices: [],
            pricingTiers: [
                { range: '< 100', price: '$0.000', priceValue: 0 },
                { range: '101 - 10,000', price: '$0.015', priceValue: 0.015 },
                { range: '10,001 - 75,000', price: '$0.014', priceValue: 0.014 },
                { range: '75,000 - 250,000', price: '$0.011', priceValue: 0.011 },
                { range: '250,001 - 500,000', price: '$0.009', priceValue: 0.009 },
                { range: '500,001 - 1,000,000', price: '$0.006', priceValue: 0.006 },
                { range: '> 1,000,000', price: '$0.004', priceValue: 0.004 }
            ]
        }
    },
    computed: {
        phoneFormattingDirty() {
            if (!this.savedPhoneFormatting) {
                return false;
            }
            return JSON.stringify(this.phoneFormatting) !== JSON.stringify(this.savedPhoneFormatting);
        },
        phonePropertiesDirty() {
            if (!this.savedPhoneProperties) {
                return false;
            }
            return JSON.stringify(this.phoneProperties) !== JSON.stringify(this.savedPhoneProperties);
        },
        hasAnyPhoneProperty() {
            return this.phoneProperties.validatePhone
                || this.phoneProperties.validateMobilePhone
                || this.phoneProperties.customProperties.length > 0;
        },
        liveSyncDirty() {
            if (this.savedLiveSyncEnabled === null) {
                return false;
            }
            return this.liveSyncEnabled !== this.savedLiveSyncEnabled;
        },
        pickablePropertyOptions() {
            const selected = new Set(this.phoneProperties.customProperties.map(p => (p || '').toLowerCase()));
            return this.availableProperties.filter(p => !selected.has((p.name || '').toLowerCase()));
        },
        monthlyValidationCapDirty() {
            return this.monthlyValidationCap !== this.savedMonthlyValidationCap;
        },
        hasUnsavedChanges() {
            return this.phoneFormattingDirty || this.phonePropertiesDirty || this.liveSyncDirty || this.monthlyValidationCapDirty;
        },
        activeRun() {
            return this.dashboard.activeRun;
        },
        recentRuns() {
            return this.dashboard.recentRuns || [];
        },
        invoiceHeaders() {
            return [
                { text: 'Date', value: 'issuedAt' },
                { text: 'Type', value: 'type' },
                { text: 'Amount', value: 'amountCents' },
                { text: 'Status', value: 'status' },
                { text: 'Invoice', value: 'hostedInvoiceUrl' }
            ];
        },
        activeRunProgress() {
            if (!this.activeRun) {
                return 0;
            }

            const total = this.activeRun.contactsWithPhone || 0;
            if (total <= 0) {
                return 0;
            }

            const done = (this.activeRun.contactsUpdated || 0) + (this.activeRun.contactsFailed || 0);
            return Math.max(0, Math.min(100, Math.round((done / total) * 100)));
        },
        historyHeaders() {
            return [
                { text: 'Run ID', value: 'syncRunId' },
                { text: 'Scope', value: 'scope' },
                { text: 'Status', value: 'status' },
                { text: 'Started', value: 'startedAt' },
                { text: 'Completed', value: 'completedAt' },
                { text: 'Created', value: 'createdAt' },
                { text: 'Updated', value: 'contactsUpdated' },
                { text: 'Failed', value: 'contactsFailed' }
            ];
        },
        usagePercent() {
            const denominator = this.usage.continuousPlanActive ? this.usage.monthlyValidationCap : this.usage.monthlyFreeLimit;
            if (!denominator) {
                return 0;
            }

            return Math.max(0, Math.min(100, Math.round((this.usage.currentMonthUsed / denominator) * 100)));
        },
        currentMonthLabel() {
            return this.monthLabel(this.usage.currentYear, this.usage.currentMonth);
        },
        previousMonthLabel() {
            return this.monthLabel(this.usage.previousYear, this.usage.previousMonth);
        },
        pricingTiersDisplay() {
            const baselineTier = this.pricingTiers.find(tier => tier.priceValue > 0);
            const baseline = baselineTier ? baselineTier.priceValue : null;

            return this.pricingTiers.map(tier => {
                if (tier.priceValue === 0) {
                    return { ...tier, badge: 'Free', badgeColor: 'grey lighten-2', badgeTextColor: 'grey darken-2' };
                }

                if (!baseline || tier.priceValue >= baseline) {
                    return { ...tier, badge: null };
                }

                const savings = Math.round((1 - tier.priceValue / baseline) * 100);
                return { ...tier, badge: `Save ${savings}%`, badgeColor: 'green lighten-5', badgeTextColor: 'green darken-2' };
            });
        }
    },
    async created() {
        await this.$auth.auth0ClientPromise;
        await this.initializePage();
    },
    destroyed() {
        this.stopPolling();
    },
    watch: {
        activeTab(newTab) {
            if (newTab === this.billingTabIndex) {
                this.loadUsage();
                this.loadInvoices();
            }
        },
        validateScope() {
            this.estimate = null;
        },
        selectedSegmentId() {
            this.estimate = null;
        }
    },
    methods: {
        async initializePage() {
            this.loading = true;
            this.handleOAuthReturnMessage();
            await this.loadStatus();

            if (this.status.hubspotConnected) {
                await this.loadSyncDashboard();
                await this.loadSegments();
            }

            if (this.status.hubspotConnected && this.status.propertiesCreated) {
                await this.loadLiveSync();
                await this.loadPhoneProperties();
                await this.loadPhoneFormatting();
                await this.loadUsage();
            }

            this.loading = false;
        },
        handleOAuthReturnMessage() {
            const result = this.$route.query.validateiq_result;
            const message = this.$route.query.message;

            if (!result) {
                return;
            }

            const toastType = result === 'success' || result === 'partial' ? 'success' : 'error';
            const title = result === 'success'
                ? 'HubSpot Connected'
                : (result === 'partial' ? 'Connected with Warnings' : 'HubSpot Connection Failed');

            this.$swal({
                icon: toastType,
                title: title,
                text: message || null,
                toast: true,
                timer: 4500,
                position: 'top-end'
            });

            const updatedQuery = Object.assign({}, this.$route.query);
            delete updatedQuery.validateiq_result;
            delete updatedQuery.message;
            this.$router.replace({ query: updatedQuery }).catch(() => {});
        },
        async loadStatus() {
            const response = await this.$store.dispatch('validateIq/getStatus');

            if (response && response.status === 200 && response.data) {
                this.status = {
                    hubspotConnected: !!response.data.hubspotConnected,
                    propertiesCreated: !!response.data.propertiesCreated
                };
                return;
            }

            this.status = {
                hubspotConnected: false,
                propertiesCreated: false
            };
            this.$swal({
                icon: 'error',
                title: 'Status Error',
                text: 'Could not load Validate IQ status. Please try again later.'
            });
        },
        async connectHubSpot() {
            this.connectingToHubSpot = true;
            const returnUrl = `${window.location.origin}${this.$route.path}`;
            const response = await this.$store.dispatch('validateIq/getConnectUrl', returnUrl);
            this.connectingToHubSpot = false;

            if (response && response.status === 200 && response.data && response.data.authorizationUrl) {
                window.location.href = response.data.authorizationUrl;
                return;
            }

            this.$swal({
                icon: 'error',
                title: 'Connection Error',
                text: 'Could not create HubSpot authorization URL.'
            });
        },
        async syncProperties() {
            this.syncingProperties = true;
            const response = await this.$store.dispatch('validateIq/syncProperties');
            this.syncingProperties = false;

            if (response && response.status === 200 && response.data && response.data.success) {
                this.$swal({
                    icon: 'success',
                    title: 'Properties Synced',
                    text: 'Validate IQ contact properties are now ready.',
                    toast: true,
                    timer: 3000,
                    position: 'top-end'
                });
                await this.loadStatus();
                await this.loadSyncDashboard();
                return;
            }

            this.$swal({
                icon: 'error',
                title: 'Sync Error',
                text: response && response.data && response.data.error ? response.data.error : 'Could not sync properties.'
            });
        },
        async loadLiveSync() {
            const response = await this.$store.dispatch('validateIq/getLiveSync');

            if (response && response.status === 200 && response.data) {
                this.liveSyncEnabled = !!response.data.enabled;
            }

            this.savedLiveSyncEnabled = this.liveSyncEnabled;
        },
        async loadUsage() {
            this.loadingUsage = true;
            const response = await this.$store.dispatch('validateIq/getUsage');
            this.loadingUsage = false;

            if (response && response.status === 200 && response.data) {
                this.usage = {
                    paymentConfigured: !!response.data.paymentConfigured,
                    monthlyFreeLimit: response.data.monthlyFreeLimit === null || response.data.monthlyFreeLimit === undefined
                        ? null
                        : response.data.monthlyFreeLimit,
                    currentYear: response.data.currentYear,
                    currentMonth: response.data.currentMonth,
                    currentMonthUsed: response.data.currentMonthUsed || 0,
                    previousYear: response.data.previousYear,
                    previousMonth: response.data.previousMonth,
                    previousMonthUsed: response.data.previousMonthUsed || 0,
                    continuousPlanActive: !!response.data.continuousPlanActive,
                    monthlyValidationCap: response.data.monthlyValidationCap === null || response.data.monthlyValidationCap === undefined
                        ? null
                        : response.data.monthlyValidationCap
                };
                this.monthlyValidationCap = this.usage.monthlyValidationCap;
                this.savedMonthlyValidationCap = this.usage.monthlyValidationCap;
            }
        },
        monthLabel(year, month) {
            if (!year || !month) {
                return '';
            }

            const date = new Date(Date.UTC(year, month - 1, 1));
            return date.toLocaleString('default', { month: 'long', year: 'numeric', timeZone: 'UTC' });
        },
        // Runs `action` immediately, unless there are unsaved Phone Properties/Formatting
        // changes - in that case it's held until the user confirms via the dialog, and
        // dropped entirely if they cancel.
        runWithUnsavedChangesGuard(action) {
            if (this.hasUnsavedChanges) {
                this.pendingConfirmedAction = action;
                this.showUnsavedChangesConfirm = true;
                return;
            }

            action();
        },
        confirmPendingAction() {
            this.showUnsavedChangesConfirm = false;
            const action = this.pendingConfirmedAction;
            this.pendingConfirmedAction = null;

            if (action) {
                action();
            }
        },
        cancelPendingAction() {
            this.showUnsavedChangesConfirm = false;
            this.pendingConfirmedAction = null;
        },
        async saveLiveSync() {
            this.savingLiveSync = true;
            const response = await this.$store.dispatch('validateIq/setLiveSync', this.liveSyncEnabled);
            this.savingLiveSync = false;

            if (response && response.status === 200 && response.data && !response.data.errorMessage) {
                this.liveSyncEnabled = !!response.data.enabled;
                this.savedLiveSyncEnabled = this.liveSyncEnabled;
                this.$swal({
                    icon: 'success',
                    title: this.liveSyncEnabled ? 'Continuous Monitoring Enabled' : 'Continuous Monitoring Disabled',
                    toast: true,
                    timer: 2000,
                    position: 'top-end'
                });
                return;
            }

            if (response && response.data && response.data.errorMessage) {
                this.liveSyncEnabled = !!response.data.enabled;
                this.savedLiveSyncEnabled = this.liveSyncEnabled;
            }

            this.$swal({
                icon: 'error',
                title: 'Save Error',
                text: response && response.data && response.data.errorMessage
                    ? response.data.errorMessage
                    : 'Could not update Continuous Monitoring setting.'
            });
        },
        cancelLiveSync() {
            this.liveSyncEnabled = this.savedLiveSyncEnabled;
        },
        buildPhoneFormattingPayload() {
            return {
                formatNumbers: this.phoneFormatting.formatNumbers,
                addCountryCode: this.phoneFormatting.addCountryCodeEnabled,
                preserveExtension: this.phoneFormatting.preserveExtension
            };
        },
        async loadPhoneFormatting() {
            const response = await this.$store.dispatch('validateIq/getPhoneFormatting');

            if (response && response.status === 200 && response.data) {
                this.phoneFormatting = {
                    formatNumbers: !!response.data.formatNumbers,
                    addCountryCodeEnabled: !!response.data.addCountryCode,
                    preserveExtension: !!response.data.preserveExtension
                };
            }

            this.savedPhoneFormatting = JSON.parse(JSON.stringify(this.phoneFormatting));
        },
        async savePhoneFormattingOptions() {
            this.savingPhoneFormatting = true;
            const response = await this.$store.dispatch('validateIq/savePhoneFormatting', this.buildPhoneFormattingPayload());
            this.savingPhoneFormatting = false;

            if (response && response.status === 200 && response.data) {
                this.savedPhoneFormatting = JSON.parse(JSON.stringify(this.phoneFormatting));
                this.$swal({
                    icon: 'success',
                    title: 'Formatting Saved',
                    toast: true,
                    timer: 2000,
                    position: 'top-end'
                });
                return;
            }

            this.$swal({
                icon: 'error',
                title: 'Save Error',
                text: 'Could not save phone formatting options.'
            });
        },
        cancelPhoneFormattingOptions() {
            this.phoneFormatting = JSON.parse(JSON.stringify(this.savedPhoneFormatting));
        },
        async loadPhoneProperties() {
            const response = await this.$store.dispatch('validateIq/getPhoneProperties');

            if (response && response.status === 200 && response.data) {
                this.phoneProperties = {
                    validatePhone: response.data.validatePhone !== false,
                    validateMobilePhone: response.data.validateMobilePhone !== false,
                    customProperties: Array.isArray(response.data.customProperties) ? response.data.customProperties.slice(0, 3) : []
                };
            }

            this.savedPhoneProperties = JSON.parse(JSON.stringify(this.phoneProperties));
        },
        buildPhonePropertiesPayload() {
            return {
                validatePhone: this.phoneProperties.validatePhone,
                validateMobilePhone: this.phoneProperties.validateMobilePhone,
                customProperties: this.phoneProperties.customProperties
            };
        },
        async savePhonePropertiesOptions() {
            this.savingPhoneProperties = true;
            const response = await this.$store.dispatch('validateIq/savePhoneProperties', this.buildPhonePropertiesPayload());
            this.savingPhoneProperties = false;

            if (response && response.status === 200 && response.data) {
                this.phoneProperties = {
                    validatePhone: response.data.validatePhone !== false,
                    validateMobilePhone: response.data.validateMobilePhone !== false,
                    customProperties: Array.isArray(response.data.customProperties) ? response.data.customProperties.slice(0, 3) : []
                };
                this.savedPhoneProperties = JSON.parse(JSON.stringify(this.phoneProperties));
                this.$swal({
                    icon: 'success',
                    title: 'Phone Properties Saved',
                    toast: true,
                    timer: 2000,
                    position: 'top-end'
                });
                return;
            }

            this.$swal({
                icon: 'error',
                title: 'Save Error',
                text: 'Could not save phone property options.'
            });
        },
        cancelPhonePropertiesOptions() {
            this.phoneProperties = JSON.parse(JSON.stringify(this.savedPhoneProperties));
        },
        async openPropertyPicker() {
            this.selectedPropertyToAdd = null;
            this.showPropertyPicker = true;

            if (this.availableProperties.length === 0) {
                await this.loadAvailableProperties();
            }
        },
        async loadAvailableProperties() {
            this.loadingAvailableProperties = true;
            const response = await this.$store.dispatch('validateIq/getAvailablePhoneProperties');
            this.loadingAvailableProperties = false;

            if (response && response.status === 200 && Array.isArray(response.data)) {
                this.availableProperties = response.data;
                return;
            }

            this.$swal({
                icon: 'error',
                title: 'Load Error',
                text: 'Could not load HubSpot contact properties.'
            });
        },
        addCustomProperty() {
            if (!this.selectedPropertyToAdd || this.phoneProperties.customProperties.length >= 3) {
                return;
            }

            if (!this.phoneProperties.customProperties.includes(this.selectedPropertyToAdd)) {
                this.phoneProperties.customProperties.push(this.selectedPropertyToAdd);
            }

            this.showPropertyPicker = false;
            this.selectedPropertyToAdd = null;
        },
        removeCustomProperty(propertyName) {
            this.phoneProperties.customProperties = this.phoneProperties.customProperties.filter(p => p !== propertyName);
        },
        propertyLabel(propertyName) {
            const found = this.availableProperties.find(p => p.name === propertyName);
            return found ? found.label : propertyName;
        },
        guardedValidateSingleContact() {
            this.runWithUnsavedChangesGuard(this.validateSingleContact);
        },
        extractHubSpotContactId(value) {
            if (!value) {
                return value;
            }

            const trimmed = value.trim();
            // HubSpot contact URLs look like
            // https://app.hubspot.com/contacts/<portalId>/record/<objectTypeId>/<contactId>[?query]
            const urlMatch = trimmed.match(/\/record\/[^/]+\/(\d+)/);
            return urlMatch ? urlMatch[1] : trimmed;
        },
        async validateSingleContact() {
            this.singleContactResult = null;
            const hsContactId = this.extractHubSpotContactId(this.singleContactId);
            if (!hsContactId) {
                this.$swal({
                    icon: 'warning',
                    title: 'Missing contact id',
                    text: 'Enter a HubSpot contact id or contact URL first.'
                });
                return;
            }

            this.validatingSingleContact = true;
            const response = await this.$store.dispatch('validateIq/validateContact', {
                hsContactId,
                phoneFormatting: this.buildPhoneFormattingPayload()
            });
            this.validatingSingleContact = false;

            if (response && response.status === 200 && response.data) {
                this.singleContactResult = response.data;
                return;
            }

            this.singleContactResult = {
                success: false,
                errorMessage: response && response.data && (response.data.errorMessage || response.data.error)
                    ? (response.data.errorMessage || response.data.error)
                    : 'Failed to validate contact.'
            };
        },
        async loadSegments() {
            this.loadingSegments = true;
            const response = await this.$store.dispatch('validateIq/getSegments');
            this.loadingSegments = false;

            if (response && response.status === 200 && response.data && response.data.success) {
                this.segments = response.data.segments || [];
                return;
            }

            this.segments = [];
        },
        guardedOpenValidateContactsDialog() {
            this.runWithUnsavedChangesGuard(this.openValidateContactsDialog);
        },
        openValidateContactsDialog() {
            this.validateScope = 'all';
            this.selectedSegmentId = null;
            this.estimate = null;
            this.showValidateContactsDialog = true;
        },
        closeValidateContactsDialog() {
            this.showValidateContactsDialog = false;
        },
        async loadEstimate() {
            this.loadingEstimate = true;
            const response = await this.$store.dispatch('validateIq/getEstimate', {
                scope: this.validateScope,
                hubSpotListId: this.validateScope === 'segment' ? this.selectedSegmentId : null
            });
            this.loadingEstimate = false;

            if (response && response.status === 200 && response.data && response.data.success) {
                this.estimate = response.data;
                return;
            }

            this.$swal({
                icon: 'error',
                title: 'Estimate Error',
                text: response && response.data && (response.data.errorMessage || response.data.error)
                    ? (response.data.errorMessage || response.data.error)
                    : 'Could not estimate validation count.'
            });
        },
        async startValidation() {
            this.startingValidation = true;
            const response = this.validateScope === 'segment'
                ? await this.$store.dispatch('validateIq/validateSegmentContacts', {
                    listId: this.selectedSegmentId,
                    phoneFormatting: this.buildPhoneFormattingPayload()
                })
                : await this.$store.dispatch('validateIq/validateAllContacts', this.buildPhoneFormattingPayload());
            this.startingValidation = false;

            if (response && response.status === 202 && response.data && response.data.success) {
                this.showValidateContactsDialog = false;
                this.$swal({
                    icon: 'success',
                    title: 'Sync Started',
                    text: response.data.message || 'Validate IQ sync has been queued.',
                    toast: true,
                    timer: 3000,
                    position: 'top-end'
                });

                await this.loadSyncDashboard();
                return;
            }

            if (response && response.status === 429) {
                this.$swal({
                    icon: 'info',
                    title: 'Sync In Progress',
                    text: response.data && response.data.errorMessage ? response.data.errorMessage : 'A sync is already in progress.'
                });
                await this.loadSyncDashboard();
                return;
            }

            this.$swal({
                icon: 'error',
                title: 'Sync Error',
                text: response && response.data && (response.data.errorMessage || response.data.error)
                    ? (response.data.errorMessage || response.data.error)
                    : 'Could not start sync.'
            });
        },
        async purchaseAndStart() {
            this.purchasingAndStarting = true;
            const response = await this.$store.dispatch('validateIq/purchaseAndStart', {
                scope: this.validateScope,
                hubSpotListId: this.validateScope === 'segment' ? this.selectedSegmentId : null
            });
            this.purchasingAndStarting = false;

            if (response && response.status === 200 && response.data && response.data.success) {
                if (response.data.checkoutUrl) {
                    window.location.href = response.data.checkoutUrl;
                    return;
                }

                // Re-derived server-side as fully covered by the free allowance after all - it
                // was started directly, same as Start Sync.
                this.showValidateContactsDialog = false;
                this.$swal({
                    icon: 'success',
                    title: 'Sync Started',
                    text: response.data.message || 'Validate IQ sync has been queued.',
                    toast: true,
                    timer: 3000,
                    position: 'top-end'
                });
                await this.loadSyncDashboard();
                return;
            }

            this.$swal({
                icon: 'error',
                title: 'Checkout Error',
                text: response && response.data && (response.data.errorMessage || response.data.error)
                    ? (response.data.errorMessage || response.data.error)
                    : 'Could not start checkout.'
            });
        },
        async startContinuousPlanCheckout() {
            this.startingContinuousPlanCheckout = true;
            const response = await this.$store.dispatch('validateIq/createContinuousPlanCheckout');
            this.startingContinuousPlanCheckout = false;

            if (response && response.status === 200 && response.data && response.data.success && response.data.checkoutUrl) {
                window.location.href = response.data.checkoutUrl;
                return;
            }

            this.$swal({
                icon: 'error',
                title: 'Checkout Error',
                text: response && response.data && (response.data.errorMessage || response.data.error)
                    ? (response.data.errorMessage || response.data.error)
                    : 'Could not start Continuous Plan checkout.'
            });
        },
        async saveMonthlyValidationCap() {
            this.savingMonthlyCap = true;
            const response = await this.$store.dispatch('validateIq/saveMonthlyCap', this.monthlyValidationCap);
            this.savingMonthlyCap = false;

            if (response && response.status === 200 && response.data && response.data.success) {
                this.savedMonthlyValidationCap = response.data.monthlyValidationCap === null || response.data.monthlyValidationCap === undefined
                    ? null
                    : response.data.monthlyValidationCap;
                this.monthlyValidationCap = this.savedMonthlyValidationCap;
                this.$swal({
                    icon: 'success',
                    title: 'Cap Saved',
                    toast: true,
                    timer: 2000,
                    position: 'top-end'
                });
                return;
            }

            this.$swal({
                icon: 'error',
                title: 'Save Error',
                text: response && response.data && (response.data.errorMessage || response.data.error)
                    ? (response.data.errorMessage || response.data.error)
                    : 'Could not save monthly validation cap.'
            });
        },
        cancelMonthlyValidationCap() {
            this.monthlyValidationCap = this.savedMonthlyValidationCap;
        },
        async loadInvoices() {
            this.loadingInvoices = true;
            const response = await this.$store.dispatch('validateIq/getInvoices');
            this.loadingInvoices = false;

            if (response && response.status === 200 && response.data && response.data.success) {
                this.invoices = response.data.invoices || [];
                return;
            }

            this.invoices = [];
        },
        formatCents(cents) {
            return `$${((cents || 0) / 100).toFixed(2)}`;
        },
        async loadSyncDashboard() {
            this.loadingDashboard = true;
            const response = await this.$store.dispatch('validateIq/getSyncDashboard', 5);
            this.loadingDashboard = false;

            if (response && response.status === 200 && response.data && response.data.success) {
                this.dashboard = {
                    activeRun: response.data.activeRun,
                    recentRuns: response.data.recentRuns || []
                };
                this.togglePolling();
                return;
            }

            this.dashboard = {
                activeRun: null,
                recentRuns: []
            };
        },
        togglePolling() {
            if (!this.activeRun) {
                this.stopPolling();
                return;
            }

            if (this.poller) {
                return;
            }

            this.poller = setInterval(async () => {
                await this.loadSyncDashboard();
            }, 10000);
        },
        stopPolling() {
            if (this.poller) {
                clearInterval(this.poller);
                this.poller = null;
            }
        },
        statusColor(status) {
            if (status === 'COMPLETED') {
                return 'success';
            }
            if (status === 'FAILED') {
                return 'error';
            }
            return 'warning';
        },
        formatDate(value) {
            if (!value) {
                return '-';
            }

            return new Date(value).toLocaleString();
        }
    }
}
</script>



